import 'reflect-metadata'

import React, { useEffect, useState } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouteObject, RouterProvider, useLocation, useNavigate, useSearchParams } from 'react-router'
import { observer } from 'mobx-react-lite'


// Dependency injection
import { container as diContainer } from '../di'
import { ContainerProvider, useService } from '@evertel/di'


// css
import './../scss/style.scss'

// Import routes
import routes from './routes'

// Import services
import { SessionService } from '@evertel/session/SessionService'
import { LogoutService } from '@evertel/session/LogoutService'
import { AppService, AuthBroadcastService, DocumentTitleProvider } from '@evertel/web/core'

// utilities
import { setAdditionalErrorInfo } from '../ErrorHandler'

// Import components and contexts
import { Page404 } from '../views/Pages'
import AuthorizedLayout from './AuthorizedLayout'
import UnAuthorizedLayout from './UnAuthorizedLayout'

import ErrorBoundary from '../utilities/errorBoundary'
import ElectronBoundary from '../utilities/ElectronBoundary'
import * as Electron from '../utilities/electronInterface'

import { UIContextProvider, useUI } from '@evertel/web/ui'
import { JoinRoomContextProvider } from '@evertel/web/room'
import { InviteContextProvider } from '@evertel/web/invites'
import { CreateRoomContextProvider } from '@evertel/web/room'
import { UserProfileModalContextProvider } from '@evertel/web/user'
import { CreateThreadContextProvider } from '@evertel/web/thread'
import { CurrentMediaPlayingProvider } from '@evertel/hooks'

// FontAwesome
import { library } from '@fortawesome/fontawesome-svg-core'
import {
    faPlay, faPause, faMusicNote,
    faPlusSquare, faUsers, faCheckSquare, faHandshake, faPlus, faPaperPlane, faBuilding, faEnvelope, faGauge,
    faCircleXmark, faEllipsisVertical, faLocationDot, faTableLayout, faTachometerAlt, faBell, faPaperPlaneTop,
    faCheckCircle, faDiamond, faCirclePlus, faShieldKeyhole, faCaretDown, faCaretRight, faStar, faFileAlt, faMessage, faComment, faUser, faFire,
    faCircleInfo as faCircleInfoSolid,
    faArrowLeft as faArrowLeftSolid
} from '@fortawesome/pro-solid-svg-icons'
import { fal } from '@fortawesome/pro-light-svg-icons'
import { faBold, faCircleInfo, faItalic, faList, faListOl, faStrikethrough, faLink, faBracketsCurly, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { faJira, faHubspot, faMailchimp, faApple, faAndroid } from '@fortawesome/free-brands-svg-icons'

import debugModule from 'debug'
if (import.meta.env.MODE !== 'production' && import.meta.env.VITE_DEBUG_WEB) {
    debugModule.enable(import.meta.env.VITE_DEBUG_WEB)
}

//import { ipcRenderer } from 'electron'

library.add(fal, faPlay, faPause, faMusicNote, faPlusSquare, faUsers, faCheckSquare, faHandshake, faPlus, faPaperPlane, faBuilding, faEnvelope, faGauge, faCircleInfo, faCircleInfoSolid,
    faCircleXmark, faEllipsisVertical, faLocationDot, faTableLayout, faTachometerAlt, faBell, faBold, faItalic, faList, faListOl, faStrikethrough,
    faLink, faBracketsCurly, faPaperPlaneTop, faCheckCircle, faDiamond, faCirclePlus, faJira, faHubspot, faMailchimp, faApple, faAndroid, faShieldKeyhole,
    faCaretDown, faCaretRight, faStar, faFileAlt, faMessage, faArrowLeft, faArrowLeftSolid, faComment, faUser, faFire)


const RootRedirect: React.FC = () => {
    const sessionService = useService(SessionService, [])

    //handle old links that might have the /#/ in the url
    const hashedUrl = window.location.hash.slice(1)
    if (hashedUrl[0] === '/') {
        return <Navigate to={`${hashedUrl}`} replace />
    }

    // Handle OAuth access token in hash
    if (hashedUrl.match(/^access_token/)) {
        return <Navigate to={`/login-sso?${hashedUrl}`} replace />
    }

    return sessionService.currentUserId
        ? (<Navigate to="/landing" replace />)
        : (<Navigate to="/login" replace />)
}

// ProtectedRoute component to handle auth check.
// Captures the attempted URL before redirecting so the user
// is taken there after login (callback URL feature).
const ProtectedRoute: React.FC = observer(() => {
    const sessionService = useService(SessionService, [])
    const location = useLocation()

    if (sessionService.currentUserId) {
        return <AuthorizedLayout />
    }

    // Capture the attempted path before redirecting to login
    const attemptedPath = location.pathname + (location.search || '')
    sessionService.setCallbackUrl(attemptedPath)

    return <Navigate to="/login" replace />
})

const App: React.FC = observer(() => {

    const [searchParams] = useSearchParams()
    const debugParam = searchParams.get('debug')
    const testParam = searchParams.get('test')

    const [hasBeenActive, setHasBeenActive] = useState(() => !document.hidden)

    const navigate = useNavigate()

    const appService = useService(AppService, [])
    const sessionService = useService(SessionService, [])
    const logoutService = useService(LogoutService)
    const authBroadcastService = useService(AuthBroadcastService, [])

    const { showAppLoader } = useUI()

    // Track visibility changes - flip hasBeenActive to true when tab becomes visible
    useEffect(() => {
        if (hasBeenActive) return // Already active, no need to listen

        const onVisibilityChange = () => {
            if (!document.hidden) {
                setHasBeenActive(true)
            }
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [hasBeenActive])

    // Lightweight pre-init - always runs immediately, even on background tabs.
    // Shows loader, loads theme (avoids bright flash on dark-theme), and starts
    // auth broadcast listener so background tabs can receive cross-tab messages.
    useEffect(() => {
        showAppLoader({ isOpen: true, type: 'full-overlay' })
        appService.preInit()
    }, [])

    // Heavy init - only runs when tab has been active at least once
    useEffect(() => {
        if (!hasBeenActive) return

        const initializeApp = async () => {
            await appService.init()

            // Set callbacks AFTER appService.init() intentionally -
            // we only want to navigate/reload on explicit logout actions,
            // not during initial auth validation
            logoutService.setGetCurrentUrl(() => {
                return window.location.pathname + (window.location.search || '')
            })
            logoutService.setOnLogoutCallback(() => {
                authBroadcastService.broadcastLogout()
                // Best-effort cleanup before hard reload. The reload below
                // reconstructs everything from scratch, so there's no re-init
                // path - this just ensures listeners/timers don't linger.
                appService.destroy()
                navigate('/login')
                window.location.reload()
            })

            // hide loading overlay (give extra time for UI to update)
            setTimeout(() => {
                showAppLoader({ isOpen: false })
            }, 300)
        }

        initializeApp()
    }, [hasBeenActive])

    // Update error handler context whenever auth state changes.
    // Mirrors mobile App.tsx setAdditionalErrorInfo pattern.
    useEffect(() => {
        const updateDebugInfo = async () => {
            if (!appService.isInitialized) return

            setAdditionalErrorInfo({
                page: window.location.pathname,
                timestamp: new Date().toISOString(),
                userInfo: { id: sessionService.currentUserId },
                ...await appService.debugInfo()
            })
        }

        updateDebugInfo()
    }, [sessionService.currentUserId, appService.isInitialized])

    // Debug/test URL params
    useEffect(() => {
        if (debugParam !== null) {
            sessionService.setDebugEnabled(['1', 'true'].includes(debugParam))
        }
        if (testParam !== null) {
            sessionService.setTestEnabled(['1', 'true'].includes(testParam))
        }
        if (debugParam !== null && testParam !== null) {
            console.log(`debug: ${sessionService.debugEnabled};  test: ${sessionService.testEnabled}`)
        }
    }, [debugParam])

    // Electron wireup (web-specific)
    useEffect(() => {
        Electron.wireUp()
    }, [])

    return (
        <>
            {appService.isInitialized &&
                <DocumentTitleProvider routes={routes}>
                    <UserProfileModalContextProvider>
                        <InviteContextProvider>
                            <CreateThreadContextProvider>
                                <CreateRoomContextProvider>
                                    <JoinRoomContextProvider>
                                        <CurrentMediaPlayingProvider>
                                            <ElectronBoundary>
                                                <Outlet />
                                            </ElectronBoundary>
                                        </CurrentMediaPlayingProvider>
                                    </JoinRoomContextProvider>
                                </CreateRoomContextProvider>
                            </CreateThreadContextProvider>
                        </InviteContextProvider>
                    </UserProfileModalContextProvider>
                </DocumentTitleProvider>
            }
            <div id="react-select-root" className="react-select"></div>
        </>
    )
})

// Create the router
export const router = createBrowserRouter([
    {
        path: '/',
        element: (
            // need to use this provider inside of <App />
            <UIContextProvider>
                <App />
            </UIContextProvider>
        ),
        errorElement: <ErrorBoundary />,
        children: [
            // Root redirect route
            {
                index: true,
                element: <RootRedirect />
            },
            // Unauth-only routes (logged-in users are redirected away)
            {
                element: <UnAuthorizedLayout />,
                children: routes.filter(route => !route.requiresAuth && route.unauthOnly) as RouteObject[]
            },
            // Auth-agnostic public routes (accessible regardless of auth state)
            ...routes.filter(route => !route.requiresAuth && !route.unauthOnly) as RouteObject[],
            // Protected routes
            {
                element: <ProtectedRoute />,
                children: routes.filter(route => route.requiresAuth) as RouteObject[]
            },
            // Catch-all route
            {
                path: '*',
                element: <Page404 />
            }
        ]
    }
])

const WrappedApp: React.FC = observer((props) => {
    return (
        <ContainerProvider value={diContainer}>
            <RouterProvider router={router} />
        </ContainerProvider>
    )
})

export default WrappedApp
